import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown } from 'lucide-react';
import { useStickToBottom } from '@hooks/useStickToBottom';
import { cn } from '@/lib/utils';

interface ChatMessageListProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Owns scrolling only; message content stays the caller's business. It follows
 * a streaming reply while the reader is at the bottom and stops the moment they
 * scroll up, so reading back through a conversation is not yanked away.
 *
 * Modelled on shadcn's MessageScroller: a viewport carrying the scroll-fade
 * mask, plus a jump-to-latest control that reports its own state through
 * `data-active` rather than being unmounted, so it can fade out.
 */
const ChatMessageList: React.FC<ChatMessageListProps> = ({ children, className }) => {
  const { t } = useTranslation();
  const { ref, isAwayFromBottom, scrollToBottom } = useStickToBottom<HTMLDivElement>();

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={ref}
        data-scrollable={isAwayFromBottom || undefined}
        className={cn(
          'scroll-fade h-full overflow-y-auto overscroll-contain px-4 pb-4',
          className,
        )}
      >
        {children}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
        <button
          type="button"
          data-active={isAwayFromBottom}
          tabIndex={isAwayFromBottom ? 0 : -1}
          aria-hidden={!isAwayFromBottom}
          title={t('chat.jumpToLatest')}
          aria-label={t('chat.jumpToLatest')}
          onClick={() => scrollToBottom('smooth')}
          className={cn(
            'grid size-9 place-items-center rounded-full border bg-popover text-foreground',
            'shadow-lg transition-[opacity,scale,box-shadow] duration-150 ease-shell',
            'hover:bg-accent hover:shadow-xl active:scale-[0.96]',
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
            'motion-reduce:transition-none motion-reduce:active:scale-100',
            // Inert rather than unmounted, so the fade out can play.
            'data-[active=false]:pointer-events-none data-[active=false]:scale-90 data-[active=false]:opacity-0',
            'data-[active=true]:pointer-events-auto data-[active=true]:opacity-100',
          )}
        >
          <ArrowDown className="size-4" strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
};

export default ChatMessageList;
